import React, { useState, useEffect, useRef } from 'react';

import SuggestionDropdown from '../shared/SuggestionDropdown';
import { useDebounce, focusRef } from '../shared/EventHelpers';
import { getIcon } from '../shared/FormatHelpers';

const ItemSelector = ({
    exlCallBack,
    iconName,
    userInput,
    setUserInput,
    item,
    setItem,
    getSuggestions,
    placeHolder,
    isCreationPermitted,
    setIsLoading,
    onSave,
    isClearable,
    autoFocus,
}) => {
    const [activeIndex, setActiveIndex] = useState(-1);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [shouldSave, setShouldSave] = useState(false);
    const [shouldSkipSuggestionFetch, setShouldSkipSuggestionFetch] = useState(false);
    const maxIndex = suggestions ? (isCreationPermitted ? suggestions.length : suggestions.length - 1) : 0;
    const icon = getIcon(iconName);
    const showNewBadge = item && item.id === null && userInput === '';
    const maxSuggestionsToShow = 50;

    const inputRef = useRef();
    useEffect(() => {
        autoFocus && focusRef(inputRef);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const debouncedInput = useDebounce(userInput, 500);
    useEffect(() => {
        if (shouldSkipSuggestionFetch) {
            setShouldSkipSuggestionFetch(false);
            return;
        }
        if (debouncedInput) {
            const updateSuggestionsAsync = async () => {
                setIsLoading(true);
                setSuggestions(await getSuggestions(userInput));
                setShowSuggestions(true);
                setIsLoading(false);
            };
            updateSuggestionsAsync();
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedInput]);

    useEffect(() => {
        if (shouldSave) {
            onSave && onSave();
            setShouldSave(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldSave]);

    const createItem = () => {
        if (userInput) {
            const duplicateContact = suggestions.some(suggestion => (suggestion.linkedOrganizationItem && suggestion.name.toUpperCase() === userInput.toUpperCase()));
            const duplicateOrganization = suggestions.some(suggestion => suggestion.name.toUpperCase() === userInput.toUpperCase() && !duplicateContact);

            if (duplicateContact) {
                const answer = window.confirm('Kontaktperson med angivet namn existerar, skapa ändå?');
                if (!answer) {
                    return;
                }
            } else if (duplicateOrganization) {
                // TODO: Implement dotterbolag hiearki
                alert('Organisation med angivet namn existerar redan.');
                return;
            }

            setItem({ id: null, name: userInput, detailedName: userInput, link: null });

        } else {
            alert('Kan inte skapa objekt utan namn.');
        }
    };

    const setSelectedItem = () => {
        if (isCreationPermitted && activeIndex === maxIndex) {
            createItem();
        } else {
            setItem(suggestions[activeIndex]);
            if (exlCallBack !== undefined) {
                exlCallBack(suggestions[activeIndex]);
            }
        }
        setShouldSkipSuggestionFetch(true);
        setActiveIndex(-1);
        setSuggestions([]);
        setShowSuggestions(false);
        setUserInput('');
        setShouldSave(true);
    };

    const scrollToIndex = (index) => {
        const divToScrollTo = document.getElementById(`suggestion-${index}`);
        if (divToScrollTo) {
            divToScrollTo.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    };

    const onMouseDown = (event) => {
        if (activeIndex >= 0) {
            setSelectedItem();
            event.preventDefault();
        }
    };

    const onKeyDown = (event) => {
        // Enter sets selected item
        if (event.keyCode === 13) {
            if (activeIndex >= 0 && activeIndex <= maxIndex) {
                setSelectedItem();
            }
            if (userInput === '') {
                setShouldSave(true);
            }
            event.preventDefault();
        }
        // Backspace deletes item
        if (event.keyCode === 8) {
            if (userInput === '' && isClearable) {
                setItem(null);
                event.preventDefault();
            }
        }

        // Up-arrow moves up in list
        else if (event.keyCode === 38) {
            if (activeIndex > -1) {
                setActiveIndex(activeIndex - 1);
                scrollToIndex(activeIndex - 1);
            }
            event.preventDefault();
        }

        // Down-arrow moves down in list
        else if (event.keyCode === 40) {
            if (activeIndex < maxIndex) {
                setActiveIndex(activeIndex + 1);
                scrollToIndex(activeIndex + 1);
            }
            event.preventDefault();
        }
    };

    const onBlur = () => {
        setShowSuggestions(false);
        if (item) {
            setUserInput('');
        }
    };

    const onFocus = () => {
        if (userInput) {
            setShowSuggestions(true);
        }
    };

    return (
        <div className='new-item-selector' onClick={() => focusRef(inputRef)}>
            <div className='d-flex flex-nowrap'>
                {icon}
                <input
                    ref={inputRef}
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    placeholder={placeHolder ?? item?.detailedName ?? item?.name ?? ''}
                />
                {showNewBadge && <div className='new-badge' />}
                {isClearable && item && userInput === '' && (
                    <div className='clear-button' onClick={() => setItem(null)}>
                        &times;
                    </div>
                )}
            </div>
            <SuggestionDropdown
                {...{
                    userInput,
                    suggestions,
                    showSuggestions,
                    activeIndex,
                    setActiveIndex,
                    onMouseDown,
                    isCreationPermitted,
                    maxSuggestionsToShow,
                }}
            />
        </div>
    );
};

export default ItemSelector;
